import React, { useMemo, useEffect, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { ChevronRight, Home, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from 'lib/auth';

export const Breadcrumbs = ({
  current,
  baseQueryParamKey = 'previous_page',
  allowedQueryParams = null,
  fallbackPath = null,
  adminName = 'Administrator',
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const { user } = useAuth();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const pathnames = location.pathname.split('/').filter(Boolean);

  const deepLink = useMemo(() => {
    const baseValue = searchParams.get(baseQueryParamKey);
    if (!baseValue) return null;

    const queryParams = new URLSearchParams();
    if (allowedQueryParams && Array.isArray(allowedQueryParams)) {
      allowedQueryParams.forEach((key) => {
        const value = searchParams.get(key);
        if (value) queryParams.append(key, value);
      });
    } else {
      for (const [key, value] of searchParams.entries()) {
        if (key !== baseQueryParamKey) {
          queryParams.append(key, value);
        }
      }
    }

    const queryString = queryParams.toString();
    return `/app/${baseValue}${queryString ? '?' + queryString : ''}`;
  }, [searchParams, baseQueryParamKey, allowedQueryParams]);

  const autoPath =
    pathnames.length > 1 ? `/${pathnames.slice(0, -1).join('/')}` : '/';
  const prevPath = deepLink || fallbackPath || autoPath;

  const formattedDate = currentTime.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const formattedTime = currentTime.toLocaleTimeString();

  return (
    <nav
      className="flex justify-between items-center w-full px-4 py-2 bg-transparent"
      aria-label="Breadcrumb"
    >
      {/* Breadcrumb Trail */}
      <ol className="flex items-center space-x-1 text-sm">
        {pathnames.map((segment, index) => {
          const to = `/${pathnames.slice(0, index + 1).join('/')}`;
          const isLast = index === pathnames.length - 1;

          return (
            <li key={to} className="flex items-center space-x-1">
              {index > 0 && (
                <span className="text-white/40 dark:text-white/30">
                  <ChevronRight size={14} />
                </span>
              )}
              {isLast ? (
                <span className="text-white font-semibold px-1 py-1 capitalize">
                  {current || segment}
                </span>
              ) : (
                <Link
                  to={to}
                  className="text-white/70 dark:text-white/60 hover:text-white transition px-1 py-1 capitalize"
                >
                  {segment === 'app' ? <Home size={16} /> : segment}
                </Link>
              )}
            </li>
          );
        })}
      </ol>

     
    </nav>
  );
};

export default Breadcrumbs;
